import { CustomButton, CustomInput, CustomModal, If } from 'components';
import { InputType } from 'components/custom-input.tsx/CustomInput';
import React, { FC, Fragment, useEffect, useState } from 'react';
import { VoidFn } from 'types/baseTypes';
import { CrossBoundaryIcon, GreenTickIcon, RedCloseIcon } from 'assets/icons';
import { useForm } from 'react-hook-form';
import Lottie from 'lottie-react';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { string, z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { USPincodeRegex } from 'containers/invoices/create-invoice/constants';
import { ButtonType } from 'components/custom-button/CustomButton';
import { useLazyGetAddressObjectByZipcodeQuery, useLazyGetStatesListQuery } from 'containers/team-profile/api';
import ListDropDownWithSearch from 'components/list-dropdown-with-search/ListDropDownWithSearch';
import { getStatesList } from 'containers/team-profile/utils';
import { EditAddressState, TeamProfileAddress } from 'containers/team-profile/teamProfile.model';

interface TeamSwitcherProps {
  open: boolean;
  onClose: VoidFn;
  initialAddress: TeamProfileAddress;
  updateAddress: (address: TeamProfileAddress) => void;
  pageState?: EditAddressState;
}

const lineItemFormSchema = z.object({
  zipCode: string()
    .min(1, { message: 'Zip code is required' })
    .refine(
      val => {
        return USPincodeRegex.test(val);
      },
      { message: 'Enter valid Zip code' }
    ),
  street: string().min(1, { message: 'Street address is required' }),
  apartment: string().optional(),
  city: string().min(1, { message: 'City is required' })
});

const EditTeamProfileAddressModal: FC<TeamSwitcherProps> = ({
  open,
  onClose,
  initialAddress,
  updateAddress,
  pageState = EditAddressState.EDIT
}) => {
  const [stateSelected, setStateSelected] = useState<{ id: string; name: string }>({
    id: 'US_STATE',
    name: initialAddress.state
  });
  const [stateError, setStateError] = useState<string>('');

  const [getStatesListQuery, getStatesListQueryState] = useLazyGetStatesListQuery();

  const [getAddressObjectByZipcodeQuery, getAddressObjectByZipcodeQueryState] = useLazyGetAddressObjectByZipcodeQuery();

  const {
    data: getStatesListData,
    isSuccess: getStatesListDataSuccess,
    isFetching: getStatesListFetching
  } = getStatesListQueryState;

  const { data: getAddressObjectByZipcodeData, isSuccess: getAddressObjectByZipcodeSuccess } =
    getAddressObjectByZipcodeQueryState;

  const { reset, formState, setError, clearErrors, getValues, setValue, trigger } = useForm({
    defaultValues: {
      zipCode: initialAddress?.zipCode || '',
      street: initialAddress?.street || '',
      apartment: initialAddress?.apartment || '',
      city: initialAddress?.city || ''
    },
    resolver: zodResolver(lineItemFormSchema)
  });

  const resetForm = () => {
    reset({
      zipCode: initialAddress?.zipCode || '',
      street: initialAddress?.street || '',
      apartment: initialAddress?.apartment || '',
      city: initialAddress?.city || ''
    });

    setStateSelected({
      id: 'US_STATE',
      name: initialAddress.state || ''
    });
    setStateError('');
  };

  useEffect(() => {
    resetForm();
  }, [initialAddress]);

  const { errors } = formState;

  useEffect(() => {
    getStatesListQuery({});
  }, []);

  const handleSave = async () => {
    // Trigger validation for all form fields
    const isFormValid = await trigger();

    // Check state validation separately
    const isStateValid = stateSelected?.name && stateSelected.name.trim() !== '';

    if (!isStateValid) {
      setStateError('State is required');
    } else {
      setStateError('');
    }

    // Only proceed if both form and state are valid
    if (isFormValid && isStateValid) {
      updateAddress({
        zipCode: getValues('zipCode'),
        street: getValues('street'),
        apartment: getValues('apartment'),
        city: getValues('city'),
        state: stateSelected?.name || ''
      });
      onClose();
    }
  };

  const getErrorMsg = (error: { type?: string; message?: string } | undefined) => {
    if (error?.type === 'invalid_type') {
      return 'Enter a valid input';
    }

    return error?.message;
  };

  useEffect(() => {
    if (getAddressObjectByZipcodeSuccess) {
      const { city, state, stateName } = getAddressObjectByZipcodeData || {};
      setValue('city', city, { shouldValidate: true });

      setStateSelected({
        id: state,
        name: stateName
      });
      setStateError('');
    }
  }, [getAddressObjectByZipcodeData]);

  const statesList = getStatesList(getStatesListData?.data?.states || []);

  const onCancelClick = () => {
    onClose();
    resetForm();
  };

  const handleStateChange = (item: { id: string; name: string }) => {
    setStateSelected(item);
    setStateError('');
  };

  const getZipCodeSuffix = () => {
    const zipCodeValue = getValues('zipCode');
    if (!zipCodeValue) return undefined;
    return errors.zipCode ? <RedCloseIcon /> : <GreenTickIcon />;
  };

  return (
    <Fragment>
      <CustomModal open={open} backgroundColor="bg-primary" widthInPx={'607px'} onOutsideClick={onCancelClick}>
        <If condition={getStatesListFetching}>
          <div className="flex w-full items-center justify-center">
            <Lottie className="h-20 w-20" animationData={LoadingSpinnerAnimation} loop={true} />
          </div>
        </If>

        <If condition={getStatesListDataSuccess}>
          <div className="flex w-full flex-col p-2">
            <div className="flex flex-row justify-end	">
              <CrossBoundaryIcon
                id="create-customer-close-button"
                className={'cursor-pointer'}
                onClick={onCancelClick}
              />
            </div>
            <div className="mb-4 px-5">
              <div className={'mb-5 font-lato text-xl font-bold text-headingGray'}>
                {pageState === EditAddressState.EDIT ? 'Edit ' : 'Add '}
                Business Address
              </div>
              <form className="w-full">
                <div className={'mt-4 font-lato text-base font-bold text-headingGray'}>Billing Address</div>

                <div className="-mx-3 mt-4 flex flex-wrap">
                  <div className="w-full px-3 md:mb-0 md:w-1/2">
                    <CustomInput
                      id="customer-zipcode"
                      type={InputType.TEXT}
                      value={getValues('zipCode') || ''}
                      onChange={value => {
                        setValue('zipCode', value, { shouldValidate: true });
                        if (value.length === 0 || value.length == 5 || (value.includes('-') && value.length == 10)) {
                          clearErrors('zipCode');
                          if (value.length !== 0) {
                            getAddressObjectByZipcodeQuery({ zipCode: value });
                          }
                        } else {
                          setError('zipCode', {
                            type: 'manual',
                            message: 'Please enter a valid zipcode'
                          });
                        }
                      }}
                      placeholder="Zipcode"
                      hasSuffix={!!getValues('zipCode')}
                      suffix={getZipCodeSuffix()}
                      error={getErrorMsg(errors.zipCode)}
                      suffixStyle="right-2 flex items-center"
                      className={`w-full appearance-none rounded bg-secondaryBtn py-3 px-4 text-17px
                    font-semibold leading-tight text-primaryText focus:bg-secondaryBtn focus:outline-none
                    ${errors.zipCode ? 'border border-error' : ''}`}
                    />
                  </div>
                  <div className="w-full px-3 md:w-1/2">
                    <CustomInput
                      id="customer-street-address"
                      type={InputType.TEXT}
                      value={getValues('street') || ''}
                      onChange={value => {
                        setValue('street', value, { shouldValidate: true });
                      }}
                      placeholder="Street Address"
                      error={getErrorMsg(errors.street)}
                      className={`w-full appearance-none rounded bg-secondaryBtn py-3 px-4 text-17px font-semibold
                    leading-tight text-primaryText focus:bg-secondaryBtn focus:outline-none
                    ${errors.street ? 'border border-error' : ''}`}
                    />
                  </div>
                </div>

                <div className="-mx-3 my-5 flex flex-wrap">
                  <div className="w-full px-3">
                    <CustomInput
                      id="customer-apt"
                      type={InputType.TEXT}
                      value={getValues('apartment') || ''}
                      onChange={value => {
                        setValue('apartment', value, { shouldValidate: true });
                      }}
                      placeholder="Apt/Unit/Suite"
                      className="w-full appearance-none rounded bg-secondaryBtn py-3 px-4 text-17px font-semibold
                    leading-tight text-primaryText focus:bg-secondaryBtn focus:outline-none"
                    />
                  </div>
                </div>

                <div className="-mx-3 my-5 flex flex-wrap">
                  <div className="w-full px-3">
                    <CustomInput
                      id="customer-city"
                      type={InputType.TEXT}
                      value={getValues('city') || ''}
                      onChange={value => {
                        setValue('city', value, { shouldValidate: true });
                      }}
                      placeholder="City"
                      error={getErrorMsg(errors.city)}
                      className={`w-full appearance-none rounded bg-secondaryBtn py-3 px-4 text-17px font-semibold
                    leading-tight text-primaryText focus:bg-secondaryBtn focus:outline-none
                    ${errors.city ? 'border border-error' : ''}`}
                    />
                  </div>
                </div>

                <div className="-mx-3 my-5 flex flex-wrap">
                  <div className="w-full px-3">
                    <div className={stateError ? 'rounded border border-error' : ''}>
                      <ListDropDownWithSearch
                        selectedItem={stateSelected}
                        setSelectedItem={handleStateChange}
                        items={statesList}
                        inputPlaceholder={'State'}
                      />
                    </div>
                    {stateError && (
                      <div className="pt-1 text-sbase font-semibold leading-[18px] tracking-[-0.3px] text-error">
                        {stateError}
                      </div>
                    )}
                  </div>
                </div>

                {/*<ListDropDownWithSearch />*/}

                <div className={'mb-7 flex w-full flex-1 flex-row justify-end'}>
                  <CustomButton
                    id="create/edit-customer-cancel-button"
                    type={ButtonType.SECONDARY}
                    className="w-24"
                    onClick={onCancelClick}>
                    <div className="font-semibold">Cancel</div>
                  </CustomButton>

                  <CustomButton id="create/update-address-button" className=" ml-5 w-24" onClick={handleSave}>
                    <div className="text-base font-semibold">
                      {pageState === EditAddressState.EDIT ? 'Update' : 'Save'}
                    </div>
                  </CustomButton>
                </div>
              </form>
            </div>
          </div>
        </If>
      </CustomModal>
    </Fragment>
  );
};

export default EditTeamProfileAddressModal;
